import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Spinner, Alert, Table, Badge, Modal, Form, Pagination, ListGroup, Accordion } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useEntidadeContext } from '../EntidadeContext';
import { useUserContext } from '../UserContext';
import { useAuthContext } from '../context/AuthContext';
import axios from 'axios';
import ScheduleSummary from '../components/dashboard/ScheduleSummary.js';
import SubscriptionStatusCard from '../components/SubscriptionStatusCard.jsx';
import ComissaoRelatorio from '../components/dashboard/ComissaoRelatorio.js';
import { format } from 'date-fns';
import Select from 'react-select';
import InputMask from 'react-input-mask';
import addPatientIcon from '../img/icoPlusPaciente.jpg';
import { useAlertModal } from '../AlertModalContext';

const normalizeString = (str) => str ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : '';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  primary:    '#3b82f6',
  primaryDark:'#2563eb',
  success:    '#10b981',
  warning:    '#f59e0b',
  danger:     '#ef4444',
  purple:     '#8b5cf6',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray600:    '#6b7280',
  gray700:    '#374151',
  gray900:    '#111827',
  white:      '#ffffff',
};

// ─── Componentes de UI reutilizáveis ─────────────────────────────────────────

const Card = ({ children, style = {}, ...props }) => (
  <div style={{
    backgroundColor: C.white,
    borderRadius: '12px',
    border: `1px solid ${C.gray200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    ...style,
  }} {...props}>
    {children}
  </div>
);

const CardHeader = ({ children, style = {} }) => (
  <div style={{
    padding: '16px 20px',
    borderBottom: `1px solid ${C.gray100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...style,
  }}>
    {children}
  </div>
);

const CardBody = ({ children, style = {} }) => (
  <div style={{ padding: '20px', ...style }}>
    {children}
  </div>
);

const Btn = ({ children, variant = 'primary', onClick, disabled, size, style = {}, ...props }) => {
  const variants = {
    primary:   { bg: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: C.white, border: 'none' },
    secondary: { bg: C.white, color: C.gray700, border: `1px solid ${C.gray200}` },
    success:   { bg: `linear-gradient(135deg, ${C.success}, #059669)`, color: C.white, border: 'none' },
    warning:   { bg: `linear-gradient(135deg, ${C.warning}, #d97706)`, color: C.white, border: 'none' },
    danger:    { bg: `linear-gradient(135deg, ${C.danger}, #dc2626)`, color: C.white, border: 'none' },
    ghost:     { bg: 'transparent', color: C.primary, border: `1px solid ${C.primary}` },
  };
  const v = variants[variant] || variants.primary;
  const padding = size === 'sm' ? '6px 12px' : '9px 18px';
  const fontSize = size === 'sm' ? '12px' : '13px';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding,
        background: v.bg,
        color: v.color,
        border: v.border,
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize,
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.15s',
        fontFamily: 'inherit',
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const BannerAlert = ({ icon, title, description, action, variant = 'info' }) => {
  const variants = {
    info:    { bg: '#eff6ff', border: '#bfdbfe', iconBg: '#dbeafe', iconColor: C.primary },
    warning: { bg: '#fffbeb', border: '#fde68a', iconBg: '#fef3c7', iconColor: C.warning },
    success: { bg: '#f0fdf4', border: '#86efac', iconBg: '#dcfce7', iconColor: C.success },
    danger:  { bg: '#fef2f2', border: '#fecaca', iconBg: '#fee2e2', iconColor: C.danger },
  };
  const v = variants[variant];

  return (
    <div style={{
      backgroundColor: v.bg,
      border: `1px solid ${v.border}`,
      borderRadius: '12px',
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      marginBottom: '16px',
    }}>
      <div style={{
        width: '40px', height: '40px', borderRadius: '10px',
        backgroundColor: v.iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '20px', flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: '700', color: C.gray900 }}>{title}</p>
        <p style={{ margin: 0, fontSize: '13px', color: C.gray600, lineHeight: '1.5' }}>{description}</p>
      </div>
      {action && (
        <div style={{ flexShrink: 0 }}>
          {action}
        </div>
      )}
    </div>
  );
};

const QuickActionBtn = ({ icon, label, sublabel, onClick, color = C.primary }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '12px 16px', width: '100%',
      backgroundColor: C.white,
      border: `1px solid ${C.gray200}`,
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      textAlign: 'left',
      fontFamily: 'inherit',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.backgroundColor = `${color}08`;
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = `0 4px 12px ${color}20`;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.borderColor = C.gray200;
      e.currentTarget.style.backgroundColor = C.white;
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    <div style={{
      width: '40px', height: '40px', borderRadius: '10px',
      backgroundColor: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '18px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div>
      <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: C.gray900 }}>{label}</p>
      {sublabel && <p style={{ margin: 0, fontSize: '11px', color: C.gray400 }}>{sublabel}</p>}
    </div>
    <div style={{ marginLeft: 'auto', color: C.gray400, fontSize: '12px' }}>›</div>
  </button>
);

// ─── Componente Principal ─────────────────────────────────────────────────────

const DashboardPage = () => {
  const navigate = useNavigate();

  const { currentEntidade, agendaConfig, isLoadingAgendaConfig, supportData, isLoadingSupportData, isLoadingEntidade } = useEntidadeContext();
  const { currentUserRole, getPermittedInternalTabs, isLoadingAccessConfig } = useUserContext();
  const { showAlertModal } = useAlertModal();
  const { authState, updatePartialSubscription, isFetchingSubscription, loggedInUser: currentUser } = useAuthContext();

  const [lowStockMaterials, setLowStockMaterials]   = useState([]);
  const [isLoading, setIsLoading]                   = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [stockChecked, setStockChecked]             = useState(false);
  const [assignmentStatus, setAssignmentStatus]     = useState({ isLoading: true, hasReception: true });
  const assignmentsCheckedForEntityIdRef            = useRef(null);
  const [isSyncing, setIsSyncing]                   = useState(false);
  const [purchaseHistory, setPurchaseHistory]       = useState([]);
  const [isLoadingHistory, setIsLoadingHistory]     = useState(false);
  const [historyError, setHistoryError]             = useState(null);
  const [historySearched, setHistorySearched]       = useState(false);
  const today                                       = new Date();
  const firstDayOfMonth                             = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const [startDate, setStartDate]                   = useState(firstDayOfMonth);
  const [endDate, setEndDate]                       = useState(today.toISOString().split('T')[0]);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages]   = useState(0);
  const [historyTotalSum, setHistoryTotalSum]       = useState(0);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [isLoadingCreate, setIsLoadingCreate]       = useState(false);
  const [newAppointment, setNewAppointment]         = useState({
    entidade_id: null, filial_id: '', especialidade_id: '', medico_id: '',
    paciente_id: '', data_agendamento: null, hora_marcada: '', tipo: 'Consulta',
    status: 'Agendada', interesses: [],
  });
  const [newPatient, setNewPatient]                 = useState({ nome: '', cpf: '', data_nascimento: '', sexo: '', email: '', dddcelular: '', celular: '' });
  const [pacientePacotesAtivos, setPacientePacotesAtivos] = useState([]);
  const [isLoadingPacotes, setIsLoadingPacotes]     = useState(false);
  const [selectedPackageId, setSelectedPackageId]   = useState(null);
  const [refreshSummary, setRefreshSummary]         = useState(0);
  const [busyDoctorsForSlot, setBusyDoctorsForSlot] = useState([]);
  const { allFuncionarios, pacientesList, servicos, especialidades, todosMedicos } = supportData;
  const [selectedServicos, setSelectedServicos]     = useState([]);
  const [pacienteSearch, setPacienteSearch]         = useState('');
  const [showPacientesDropdown, setShowPacientesDropdown] = useState(false);
  const pacienteDropdownRef                         = useRef(null);
  const [showCreatePatientModal, setShowCreatePatientModal] = useState(false);
  const [isCreatingPatient, setIsCreatingPatient]   = useState(false);
  const [showHelpCenterModal, setShowHelpCenterModal] = useState(false);
  const [activeAgendaVideo, setActiveAgendaVideo]   = useState(null);
  const [activePacientesVideo, setActivePacientesVideo] = useState(null);
  const [activeFinanceiroVideo, setActiveFinanceiroVideo] = useState(null);
  const [activePessoalVideo, setActivePessoalVideo] = useState(null);
  const [activeEstoqueVideo, setActiveEstoqueVideo] = useState(null);
  const [activeSegurancaVideo, setActiveSegurancaVideo] = useState(null);
  const [activeConfiguracoesVideo, setActiveConfiguracoesVideo] = useState(null);
  const [activeAssinaturaVideo, setActiveAssinaturaVideo] = useState(null);
  const [helpSearchTerm, setHelpSearchTerm]         = useState('');

  // ── Permissões ──────────────────────────────────────────────────────────────
  const dashboardPermissions = useMemo(() => getPermittedInternalTabs('dashboard', currentUserRole), [getPermittedInternalTabs, currentUserRole, isLoadingAccessConfig]);
  const hasPermission = useCallback((key) => dashboardPermissions.some(tab => tab.key === key), [dashboardPermissions]);

  const getFeriadoNome = useCallback((date) => {
    if (!date || !agendaConfig?.feriados) return null;
    const ds = format(date, 'yyyy-MM-dd');
    const f = agendaConfig.feriados.find(f => f.data === ds);
    return f ? f.descricao : null;
  }, [agendaConfig]);

  const isDiaBloqueado = useCallback((date) => {
    if (!date || !agendaConfig?.diasBloqueados) return false;
    const ds = format(date, 'yyyy-MM-dd');
    return agendaConfig.diasBloqueados.some(b => b.data === ds);
  }, [agendaConfig]);

  // ── Effects (lógica original preservada) ───────────────────────────────────
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      r => r,
      error => {
        if (error.response?.status === 403 && error.response.data?.message?.includes('Sua assinatura não está ativa')) {
          showAlertModal('Assinatura Inativa', `${error.response.data.message} Clique em OK para ser redirecionado.`, 'warning', () => navigate('/pricing'));
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [showAlertModal, navigate]);

  useEffect(() => {
    if (!isLoadingEntidade && !isLoadingAgendaConfig && currentEntidade && currentUserRole === 'Representante Legal') {
      const activeModules = currentEntidade.modulos_ativos;
      const hasClinicalModule = !Array.isArray(activeModules) || activeModules.includes('consultorio');
      if (!hasClinicalModule) return;

      const h = currentEntidade.agenda_horarios_gerados;
      const ok = h && Object.values(h).some(d => (d['Manhã']?.length > 0) || (d['Tarde']?.length > 0) || (d['Noite']?.length > 0));
      if (!ok) showAlertModal('Configuração da Agenda Pendente', 'Os horários ainda não foram definidos. Clique em OK para configurar.', 'warning', () => navigate('/configuracoes'));
    }
  }, [currentEntidade, isLoadingEntidade, isLoadingAgendaConfig, currentUserRole, showAlertModal, navigate]);

  useEffect(() => { setIsLoadingSubscription(isFetchingSubscription); }, [isFetchingSubscription]);

  useEffect(() => {
    const check = async () => {
      if (currentUserRole === 'Representante Legal' && currentEntidade?.id && assignmentsCheckedForEntityIdRef.current !== currentEntidade.id) {
        assignmentsCheckedForEntityIdRef.current = currentEntidade.id;
        setAssignmentStatus(p => ({ ...p, isLoading: true }));
        try {
          const r = await axios.get(`/api/entidades/${currentEntidade.id}/profile-assignments-status`);
          setAssignmentStatus({ isLoading: false, hasReception: r.data.has_reception || false });
        } catch { setAssignmentStatus({ isLoading: false, hasReception: true }); }
      } else if (currentUserRole !== 'Representante Legal') {
        setAssignmentStatus({ isLoading: false, hasReception: true });
      }
    };
    check();
  }, [currentEntidade, currentUserRole]);

  useEffect(() => {
    const fetch = async () => {
      if (showAppointmentModal && newAppointment.paciente_id) {
        setIsLoadingPacotes(true);
        try {
          const r = await axios.get(`/api/pacientes/${newAppointment.paciente_id}/pacotes-ativos`);
          setPacientePacotesAtivos(r.data.pacotes_ativos || []);
        } catch { setPacientePacotesAtivos([]); }
        finally { setIsLoadingPacotes(false); }
      } else { setPacientePacotesAtivos([]); setSelectedPackageId(null); }
    };
    fetch();
  }, [newAppointment.paciente_id, showAppointmentModal]);

  useEffect(() => {
    const handler = (e) => { if (pacienteDropdownRef.current && !pacienteDropdownRef.current.contains(e.target)) setShowPacientesDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Handlers (lógica original preservada) ──────────────────────────────────
  const handleFetchHistory = useCallback(async (page = 1) => {
    if (currentUserRole !== 'Representante Legal') return;
    setIsLoadingHistory(true); setHistoryError(null);
    try {
      const r = await axios.get('/api/subscriptions/history', { params: { data_inicio: startDate, data_fim: endDate, page } });
      setPurchaseHistory(r.data.data || []);
      setHistoryCurrentPage(r.data.current_page);
      setHistoryTotalPages(r.data.last_page);
      setHistoryTotalSum(r.data.total_sum || 0);
      setHistorySearched(true);
    } catch { setHistoryError('Não foi possível carregar o histórico.'); setPurchaseHistory([]); setHistoryTotalSum(0); }
    finally { setIsLoadingHistory(false); }
  }, [currentUserRole, startDate, endDate]);

  const handleOpenAppointmentModal = (time, date, busyDoctorIds = []) => {
    const medicoId = currentUserRole === 'Médico' ? currentUser.id : '';
    const medico = todosMedicos.find(m => m.id === medicoId);
    setBusyDoctorsForSlot(busyDoctorIds);
    setShowAppointmentModal(true);
    setSelectedServicos([]);
    setPacienteSearch('');
    setNewAppointment({ entidade_id: currentEntidade.id, filial_id: '', especialidade_id: medico?.especialidade_id || '', medico_id: medicoId, paciente_id: '', data_agendamento: format(date, 'yyyy-MM-dd'), hora_marcada: time, tipo: 'Consulta', status: 'Agendada', interesses: [] });
  };

  const handleCloseAppointmentModal = () => { setShowAppointmentModal(false); setPacienteSearch(''); setSelectedPackageId(null); setBusyDoctorsForSlot([]); };

  const handleModalFormChange = (e) => {
    const { name, value } = e.target;
    setNewAppointment(prev => { const s = { ...prev, [name]: value }; if (name === 'especialidade_id') s.medico_id = ''; return s; });
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.paciente_id) { showAlertModal('Validação', 'Selecione um paciente.', 'warning'); return; }
    if (!newAppointment.medico_id) { showAlertModal('Validação', 'Selecione um médico.', 'warning'); return; }
    setIsLoadingCreate(true);
    const data = { ...newAppointment, servicos: selectedServicos, pacote_id: selectedPackageId };
    delete data.interesses;
    try {
      const r = await axios.post('/api/agendamentos', data);
      if (r.status === 201) { setRefreshSummary(c => c + 1); handleCloseAppointmentModal(); showAlertModal('Sucesso', 'Agendamento criado!', 'success'); }
    } catch (e) { showAlertModal('Erro', e.response?.data?.message || 'Erro ao criar agendamento.', 'danger'); }
    finally { setIsLoadingCreate(false); }
  };

  const handleServicoChange = (servicoId, pacoteId = null) => {
    const sel = selectedServicos.includes(servicoId);
    if (pacoteId) { if (sel) { setSelectedServicos([]); setSelectedPackageId(null); } else { setSelectedServicos([servicoId]); setSelectedPackageId(pacoteId); } }
    else { if (selectedPackageId) return; setSelectedServicos(prev => sel ? prev.filter(id => id !== servicoId) : [...prev, servicoId]); }
  };

  const handleCreatePatient = async () => {
    setIsCreatingPatient(true);
    if (!newPatient.nome || !newPatient.dddcelular || !newPatient.celular) { showAlertModal('Validação', 'Nome e Celular são obrigatórios.', 'warning'); setIsCreatingPatient(false); return; }
    try {
      const r = await axios.post('/api/pacientes', { ...newPatient, entidade_id: currentEntidade.id });
      const p = r.data.paciente;
      showAlertModal('Sucesso', `Paciente "${p.nome}" criado!`, 'success');
      setShowCreatePatientModal(false);
      setNewAppointment(prev => ({ ...prev, paciente_id: p.id }));
      setPacienteSearch(p.nome);
    } catch (e) { showAlertModal('Erro', `Erro: ${e.response?.data?.message || e.message}`, 'danger'); }
    finally { setIsCreatingPatient(false); }
  };

  const handleNewPatientPhoneChange = (e) => {
    const n = e.target.value.replace(/\D/g, '');
    setNewPatient(prev => ({ ...prev, dddcelular: n.substring(0, 2), celular: n.substring(2) }));
  };

  const handleSyncUsage = async () => {
    setIsSyncing(true);
    try { const r = await axios.post('/api/subscriptions/sync-usage'); updatePartialSubscription(r.data); }
    catch (e) { console.error(e); }
    finally { setIsSyncing(false); }
  };

  const handleCheckLowStock = async () => {
    if (!currentEntidade?.id) return;
    setIsLoading(true); setStockChecked(false);
    try { const r = await axios.get(`/api/estoque/baixo?entidade_id=${currentEntidade.id}`); setLowStockMaterials(r.data.materiais || []); }
    catch { setLowStockMaterials([]); }
    finally { setIsLoading(false); setStockChecked(true); }
  };

  const handleCloseHelpCenterModal = () => {
    setShowHelpCenterModal(false);
    setActiveAgendaVideo(null); setActivePacientesVideo(null); setActiveFinanceiroVideo(null);
    setActivePessoalVideo(null); setActiveSegurancaVideo(null); setActiveConfiguracoesVideo(null);
    setActiveAssinaturaVideo(null); setHelpSearchTerm('');
  };

  const filteredPacientes = useMemo(() => {
    if (!pacienteSearch) return pacientesList;
    const t = normalizeString(pacienteSearch);
    return pacientesList.filter(p => normalizeString(p.nome).includes(t));
  }, [pacienteSearch, pacientesList]);

  const formatCurrency = (v) => v == null ? 'N/A' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';

  // ── Loading states ──────────────────────────────────────────────────────────
  if (isLoadingSubscription) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: `3px solid ${C.gray200}`, borderTopColor: C.primary, animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: C.gray400, fontSize: '14px' }}>Carregando painel...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!currentEntidade) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ backgroundColor: C.gray50, minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

        {/* ── Hero Header ── */}
        <div style={{
          background: `linear-gradient(135deg, #1e3a5f 0%, #1e40af 50%, #3b82f6 100%)`,
          padding: '28px 32px 80px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decoração de fundo */}
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)' }} />
          <div style={{ position: 'absolute', bottom: '-60px', right: '15%', width: '150px', height: '150px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.04)' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'rgba(255,255,255,0.65)', fontWeight: '500' }}>
                  {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: C.white }}>
                  Olá, {currentUser?.nome?.split(' ')[0] || 'bem-vindo(a)'} 👋
                </h1>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                  {currentEntidade?.nome} · {currentUserRole}
                </p>
              </div>

              {/* Stats rápidas no header */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Pacientes', value: currentEntidade?.pacientes_count || '—', icon: '👥' },
                  { label: 'Hoje', value: currentEntidade?.agendamentos_hoje || '—', icon: '📅' },
                ].map(stat => (
                  <div key={stat.label} style={{
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '10px',
                    padding: '10px 16px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    textAlign: 'center',
                    minWidth: '80px',
                  }}>
                    <div style={{ fontSize: '18px', marginBottom: '2px' }}>{stat.icon}</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: C.white }}>{stat.value}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.65)' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Conteúdo principal (sobrepõe o header) ── */}
        <div style={{ padding: '0 24px 32px', marginTop: '-48px', position: 'relative', zIndex: 2 }}>

          {/* ── Alertas / Banners ── */}
          <div style={{ marginBottom: '20px' }}>
            {currentEntidade && !currentEntidade.has_pacientes && (
              <BannerAlert
                variant="info" icon="🏥"
                title="Próximo Passo: Cadastre seu primeiro Paciente"
                description="Sua clínica está configurada! Cadastre seu primeiro paciente para usar a agenda e o prontuário."
                action={<Btn onClick={() => navigate('/consultorio', { state: { activeTab: 'pacientes' } })}>Cadastrar Agora</Btn>}
              />
            )}
            {!assignmentStatus.isLoading && !assignmentStatus.hasReception && currentUserRole === 'Representante Legal' && (
              <BannerAlert
                variant="warning" icon="⚠️"
                title="Ação Recomendada: Atribua Perfis de Acesso"
                description="Pelo menos um colaborador deve ter o perfil de Recepção para gerenciar a agenda e registrar pagamentos."
                action={<Btn variant="warning" onClick={() => navigate('/pessoal')}>Atribuir Agora</Btn>}
              />
            )}
          </div>

          {/* ── Resumo da Agenda ── */}
          {hasPermission('resumo_agenda') && (
            <div style={{ marginBottom: '20px' }}>
              <ScheduleSummary
                onVacantSlotClick={handleOpenAppointmentModal}
                refreshTrigger={refreshSummary}
                currentEntidade={currentEntidade}
                currentUser={currentUser}
                currentUserRole={currentUserRole}
                todosMedicos={todosMedicos}
                getFeriadoNome={getFeriadoNome}
                isDiaBloqueado={isDiaBloqueado}
              />
            </div>
          )}

          {/* ── Grid principal ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>

            {/* Card: Assinatura */}
            {hasPermission('status_assinatura') && authState.subscription && (
              <SubscriptionStatusCard
                subscription={authState.subscription}
                currentUserRole={currentUserRole}
                onSyncUsage={handleSyncUsage}
                isSyncing={isSyncing}
                navigate={navigate}
              />
            )}

            {/* Card: Histórico de Compras */}
            {hasPermission('historico_assinatura') && (
              <Card>
                <CardHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📜</div>
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.gray900 }}>Histórico de Créditos</p>
                      <p style={{ margin: 0, fontSize: '11px', color: C.gray400 }}>Compras de créditos avulsos</p>
                    </div>
                  </div>
                  {historySearched && !isLoadingHistory && historyTotalSum > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '11px', color: C.gray400 }}>Total do período</p>
                      <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.success }}>{formatCurrency(historyTotalSum)}</p>
                    </div>
                  )}
                </CardHeader>
                <CardBody>
                  {/* Filtros de data */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                    {[
                      { label: 'Data Início', value: startDate, onChange: e => setStartDate(e.target.value) },
                      { label: 'Data Fim', value: endDate, onChange: e => setEndDate(e.target.value) },
                    ].map(f => (
                      <div key={f.label}>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: C.gray600, display: 'block', marginBottom: '4px' }}>{f.label}</label>
                        <input type="date" value={f.value} onChange={f.onChange} style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.gray200}`, borderRadius: '7px', fontSize: '13px', color: C.gray700, outline: 'none', fontFamily: 'inherit' }} />
                      </div>
                    ))}
                  </div>
                  <Btn onClick={() => handleFetchHistory(1)} disabled={isLoadingHistory} style={{ width: '100%', justifyContent: 'center', marginBottom: '12px' }}>
                    {isLoadingHistory ? <><span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Buscando...</> : '🔍 Buscar Histórico'}
                  </Btn>

                  {historyError && <div style={{ padding: '10px', backgroundColor: '#fef2f2', borderRadius: '8px', color: C.danger, fontSize: '13px' }}>{historyError}</div>}

                  {historySearched && !isLoadingHistory && !historyError && (
                    purchaseHistory.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: C.gray400, fontSize: '13px' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
                        Nenhuma compra encontrada no período.
                      </div>
                    ) : (
                      <>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', borderRadius: '8px', border: `1px solid ${C.gray100}` }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead>
                              <tr style={{ backgroundColor: C.gray50 }}>
                                {['Data', 'Créditos', 'Valor'].map(h => (
                                  <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Valor' ? 'right' : 'left', fontWeight: '600', color: C.gray600, borderBottom: `1px solid ${C.gray200}` }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseHistory.map(item => (
                                <tr key={item.id || item.pedido_pagarme_id} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                                  <td style={{ padding: '8px 12px', color: C.gray700 }}>{formatDate(item.created_at)}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center', color: C.gray700 }}>{item.appointments_amount}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: C.success }}>{formatCurrency(item.price)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {historyTotalPages > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '10px' }}>
                            {[...Array(historyTotalPages).keys()].map(n => (
                              <button key={n} onClick={() => handleFetchHistory(n + 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', border: `1px solid ${n + 1 === historyCurrentPage ? C.primary : C.gray200}`, backgroundColor: n + 1 === historyCurrentPage ? C.primary : C.white, color: n + 1 === historyCurrentPage ? C.white : C.gray600, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                {n + 1}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )
                  )}
                </CardBody>
              </Card>
            )}

            {/* Card: Ações Rápidas */}
            {(hasPermission('acoes_agendas') || hasPermission('acoes_financeiro') || hasPermission('acoes_equipe') || hasPermission('acoes_auditoria') || hasPermission('central_ajuda')) && (
              <Card>
                <CardHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>⚡</div>
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.gray900 }}>Ações Rápidas</p>
                      <p style={{ margin: 0, fontSize: '11px', color: C.gray400 }}>Acesse as principais áreas</p>
                    </div>
                  </div>
                </CardHeader>
                <CardBody style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {hasPermission('acoes_agendas') && <QuickActionBtn icon="📅" label="Agenda e Consultas" sublabel="Ver agendamentos do dia" onClick={() => navigate('/consultorio', { state: { activeTab: 'agenda' } })} color={C.primary} />}
                    {hasPermission('acoes_financeiro') && <QuickActionBtn icon="💰" label="Financeiro" sublabel="Pagamentos e relatórios" onClick={() => navigate('/financeiro')} color={C.success} />}
                    {hasPermission('acoes_equipe') && <QuickActionBtn icon="👥" label="Gerenciar Equipe" sublabel="Colaboradores e perfis" onClick={() => navigate('/pessoal')} color={C.purple} />}
                    {hasPermission('acoes_auditoria') && <QuickActionBtn icon="👁️" label="Logs de Auditoria" sublabel="Histórico de ações" onClick={() => navigate('/auditoria')} color={C.gray600} />}
                    {hasPermission('central_ajuda') && <QuickActionBtn icon="💡" label="Central de Ajuda" sublabel="Tutoriais em vídeo" onClick={() => setShowHelpCenterModal(true)} color={C.warning} />}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Card: Estoque Baixo */}
            {hasPermission('estoque_baixo') && (
              <Card>
                <CardHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📦</div>
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.gray900 }}>Estoque Baixo</p>
                      <p style={{ margin: 0, fontSize: '11px', color: C.gray400 }}>Materiais abaixo do mínimo</p>
                    </div>
                  </div>
                  <Btn variant="warning" size="sm" onClick={handleCheckLowStock} disabled={isLoading}>
                    {isLoading ? <><span style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Verificando</> : '🔍 Verificar'}
                  </Btn>
                </CardHeader>
                <CardBody>
                  {!stockChecked && !isLoading && (
                    <div style={{ textAlign: 'center', padding: '24px', color: C.gray400 }}>
                      <div style={{ fontSize: '40px', marginBottom: '8px' }}>📦</div>
                      <p style={{ margin: 0, fontSize: '13px' }}>Clique em "Verificar" para checar o estoque.</p>
                    </div>
                  )}
                  {stockChecked && !isLoading && (
                    lowStockMaterials.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '24px' }}>
                        <div style={{ fontSize: '40px', marginBottom: '8px' }}>✅</div>
                        <p style={{ margin: 0, fontSize: '13px', color: C.success, fontWeight: '600' }}>Estoque em dia!</p>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.gray400 }}>Nenhum material abaixo do mínimo.</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ padding: '8px 12px', backgroundColor: '#fffbeb', borderRadius: '8px', marginBottom: '12px', fontSize: '12px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          ⚠️ <strong>{lowStockMaterials.length} material(is)</strong> precisam de reposição.
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {lowStockMaterials.map(m => (
                            <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.gray100}` }}>
                              <span style={{ fontSize: '13px', color: C.gray700 }}>{m.tipo_material.nome}</span>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: C.danger }}>{m.quantidade_em_estoque} {m.tipo_material.unidade_medida}</span>
                                <span style={{ fontSize: '11px', color: C.gray400 }}>/ {m.estoque_minimo}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginTop: '12px', textAlign: 'right' }}>
                          <Btn variant="ghost" size="sm" onClick={() => navigate('/estoque')}>Gerenciar Estoque →</Btn>
                        </div>
                      </>
                    )
                  )}
                </CardBody>
              </Card>
            )}
          </div>

          {/* ── Comissões ── */}
          {hasPermission('comissoes_a_pagar') && (
            <div style={{ marginBottom: '20px' }}>
              <ComissaoRelatorio currentEntidade={currentEntidade} currentUserRole={currentUserRole} allFuncionarios={allFuncionarios} />
            </div>
          )}
        </div>
      </div>

      {/* ── Modais (lógica 100% preservada) ── */}
      <Modal show={showAppointmentModal} onHide={handleCloseAppointmentModal} size="lg">
        <Modal.Header closeButton><Modal.Title>Novo Agendamento</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            {(currentUserRole === 'Representante Legal' || currentUserRole === 'Recepção') && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <Form.Group>
                  <Form.Label>Especialidade *</Form.Label>
                  <Form.Control as="select" name="especialidade_id" value={newAppointment.especialidade_id} onChange={handleModalFormChange}>
                    <option value="">Selecione...</option>
                    {especialidades.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Médico *</Form.Label>
                  <Form.Control as="select" name="medico_id" value={newAppointment.medico_id} onChange={handleModalFormChange} disabled={!newAppointment.especialidade_id}>
                    <option value="">Selecione a especialidade</option>
                    {todosMedicos.filter(m => m.especialidade_id === parseInt(newAppointment.especialidade_id)).filter(m => !busyDoctorsForSlot.includes(m.id)).map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                  </Form.Control>
                </Form.Group>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '16px' }}>
              <Form.Group ref={pacienteDropdownRef} style={{ flex: 1, position: 'relative' }}>
                <Form.Label>Paciente *</Form.Label>
                <Form.Control type="text" placeholder="Digite para buscar..." value={pacienteSearch} onChange={e => { setPacienteSearch(e.target.value); setShowPacientesDropdown(true); if (newAppointment.paciente_id) { setNewAppointment({ ...newAppointment, paciente_id: '' }); setSelectedPackageId(null); setSelectedServicos([]); } }} onFocus={() => setShowPacientesDropdown(true)} autoComplete="off" />
                {showPacientesDropdown && (
                  <ListGroup style={{
                    position: 'absolute', zIndex: 1000, width: '100%',
                    maxHeight: '200px', overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}>
                    {filteredPacientes.length > 0
                      ? filteredPacientes.slice(0, 6).map(p => (
                          <ListGroup.Item
                            key={p.id}
                            action
                            onClick={() => {
                              setNewAppointment({ ...newAppointment, paciente_id: p.id });
                              setPacienteSearch(p.nome);
                              setShowPacientesDropdown(false);
                            }}
                          >
                            {p.nome}
                          </ListGroup.Item>
                        ))
                      : (
                          <ListGroup.Item disabled>
                            Nenhum paciente encontrado.
                          </ListGroup.Item>
                        )
                    }
                  </ListGroup>
                )}
              </Form.Group>
              <img src={addPatientIcon} alt="Novo paciente" title="Cadastrar novo paciente" style={{ width: '36px', height: '36px', cursor: 'pointer', borderRadius: '8px', marginBottom: '2px' }} onClick={() => setShowCreatePatientModal(true)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <Form.Group><Form.Label>Data</Form.Label><Form.Control type="text" value={newAppointment.data_agendamento ? new Date(newAppointment.data_agendamento + 'T00:00:00').toLocaleDateString('pt-BR') : ''} readOnly /></Form.Group>
              <Form.Group><Form.Label>Hora</Form.Label><Form.Control type="text" value={newAppointment.hora_marcada || ''} readOnly /></Form.Group>
            </div>
            {isLoadingPacotes ? <div style={{ textAlign: 'center', padding: '8px', color: C.gray400, fontSize: '13px' }}>Verificando pacotes...</div> : pacientePacotesAtivos.length > 0 && <div style={{ padding: '10px 14px', backgroundColor: '#eff6ff', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: C.primary }}>ℹ️ Este paciente possui pacotes ativos.</div>}
            <Form.Group>
              <Form.Label>Serviços</Form.Label>
              <Select isMulti options={servicos.map(s => { const p = pacientePacotesAtivos.find(pa => pa.servico_id === s.id); return { value: s.id, label: `${s.nome}${p ? ` (Pacote - ${p.sessoes_restantes} restantes)` : ''}`, pacoteId: p?.id || null, isDisabled: !p && selectedPackageId !== null }; })} value={servicos.filter(s => selectedServicos.includes(s.id)).map(s => ({ value: s.id, label: s.nome }))} onChange={opts => { const last = opts[opts.length - 1]; handleServicoChange(last?.value, last?.pacoteId); }} placeholder="Buscar serviços..." noOptionsMessage={() => 'Nenhum serviço encontrado'} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={handleCloseAppointmentModal} style={{ padding: '8px 16px', backgroundColor: C.gray100, color: C.gray700, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Cancelar</button>
          <Btn onClick={handleCreateAppointment} disabled={isLoadingCreate}>
            {isLoadingCreate ? <><span style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Agendando...</> : '✓ Agendar'}
          </Btn>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreatePatientModal} onHide={() => setShowCreatePatientModal(false)}>
        <Modal.Header closeButton><Modal.Title>Cadastrar Novo Paciente</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            {[{ label: 'Nome *', name: 'nome', type: 'text' }, { label: 'Email', name: 'email', type: 'email' }, { label: 'Data de Nascimento', name: 'data_nascimento', type: 'date' }].map(f => (
              <Form.Group key={f.name} className="mb-3">
                <Form.Label>{f.label}</Form.Label>
                <Form.Control type={f.type} name={f.name} value={newPatient[f.name]} onChange={e => setNewPatient(prev => ({ ...prev, [e.target.name]: e.target.value }))} />
              </Form.Group>
            ))}
            <Form.Group className="mb-3">
              <Form.Label>Celular *</Form.Label>
              <InputMask mask="(99) 99999-9999" value={`${newPatient.dddcelular}${newPatient.celular}`} onChange={handleNewPatientPhoneChange}>
                {(inputProps) => <Form.Control {...inputProps} type="tel" />}
              </InputMask>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => setShowCreatePatientModal(false)} style={{ padding: '8px 16px', backgroundColor: C.gray100, color: C.gray700, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Cancelar</button>
          <Btn onClick={handleCreatePatient} disabled={isCreatingPatient}>
            {isCreatingPatient ? 'Cadastrando...' : '✓ Cadastrar'}
          </Btn>
        </Modal.Footer>
      </Modal>

      <HelpCenterModal
        show={showHelpCenterModal} onHide={handleCloseHelpCenterModal}
        searchTerm={helpSearchTerm} onSearchChange={e => setHelpSearchTerm(e.target.value)}
        activeKeys={{ agenda: activeAgendaVideo, pacientes: activePacientesVideo, financeiro: activeFinanceiroVideo, pessoal: activePessoalVideo, estoque: activeEstoqueVideo, seguranca: activeSegurancaVideo, configuracoes: activeConfiguracoesVideo, assinatura: activeAssinaturaVideo }}
        onSelectHandlers={{ agenda: setActiveAgendaVideo, pacientes: setActivePacientesVideo, financeiro: setActiveFinanceiroVideo, pessoal: setActivePessoalVideo, estoque: setActiveEstoqueVideo, seguranca: setActiveSegurancaVideo, configuracoes: setActiveConfiguracoesVideo, assinatura: setActiveAssinaturaVideo }}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

// ─── HelpCenterModal (lógica preservada, visual melhorado) ────────────────────

const allHelpTopics = [
  { categoryKey: '0', categoryTitle: '🗓️ Agenda e Atendimento', stateKey: 'agenda', items: [
    { itemKey: '0-1', title: 'Como visualizar a agenda da semana?', videoId: '4sSePNyXtCs' },
    { itemKey: '0-2', title: 'Como realizar um agendamento?', videoId: 'nPHStgMJswk' },
    { itemKey: '0-4', title: 'Por que realizar check-in no agendamento?', videoId: '9EkLIuLWH7k' },
    { itemKey: '0-5', title: 'Como registrar a consulta no prontuário?', videoId: 'kKNZsb-0E1U' },
  ]},
  { categoryKey: '1', categoryTitle: '👥 Pacientes e Convênios', stateKey: 'pacientes', items: [
    { itemKey: '1-2', title: 'Como cadastrar um convênio?', videoId: '5OM9A8YUjvg' },
    { itemKey: '1-3', title: 'Como vincular um serviço a um convênio?', videoId: '5OM9A8YUjvg' },
    { itemKey: '1-4', title: 'Como vincular um convênio a um paciente?', videoId: '1lfkBNSrGR4' },
  ]},
  { categoryKey: '2', categoryTitle: '💰 Financeiro', stateKey: 'financeiro', items: [
    { itemKey: '2-1', title: 'Como realizar um pagamento?', videoId: 'e_MTXpr52F0' },
    { itemKey: '2-2', title: 'Como cancelar um pagamento?', videoId: 'KfDYAEJ2Rsg' },
    { itemKey: '2-3', title: 'Como dar desconto em um pagamento?', videoId: 'e_MTXpr52F0' },
    { itemKey: '2-5', title: 'Como lançar despesas da clínica?', videoId: 'h4_PpNr_bS8' },
  ]},
];

const HelpCenterModal = ({ show, onHide, searchTerm, onSearchChange, activeKeys, onSelectHandlers }) => {
  const filteredTopics = useMemo(() => {
    if (!searchTerm) return allHelpTopics;
    const s = normalizeString(searchTerm);
    return allHelpTopics.map(c => { const items = c.items.filter(i => normalizeString(i.title).includes(s)); return items.length ? { ...c, items } : null; }).filter(Boolean);
  }, [searchTerm]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.gray100}` }}>
        <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>💡</div>
          Central de Ajuda
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px' }}>
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: C.gray600 }}>Encontre tutoriais em vídeo para aproveitar ao máximo o sistema.</p>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: C.gray400, fontSize: '14px' }}>🔍</span>
          <input type="text" placeholder="Pesquisar tópico..." value={searchTerm} onChange={onSearchChange} style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1px solid ${C.gray200}`, borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'inherit', color: C.gray700 }} />
        </div>
        <Accordion defaultActiveKey={searchTerm ? filteredTopics.map(c => c.categoryKey) : []} alwaysOpen={!!searchTerm}>
          {filteredTopics.length > 0 ? filteredTopics.map(category => (
            <Accordion.Item eventKey={category.categoryKey} key={category.categoryKey} style={{ border: `1px solid ${C.gray200}`, borderRadius: '8px', marginBottom: '8px', overflow: 'hidden' }}>
              <Accordion.Header>{category.categoryTitle}</Accordion.Header>
              <Accordion.Body style={{ padding: '8px' }}>
                <Accordion flush activeKey={activeKeys[category.stateKey]} onSelect={onSelectHandlers[category.stateKey]}>
                  {category.items.map(item => (
                    <Accordion.Item eventKey={item.itemKey} key={item.itemKey} style={{ border: `1px solid ${C.gray100}`, borderRadius: '6px', marginBottom: '4px', overflow: 'hidden' }}>
                      <Accordion.Header as="h6" style={{ fontSize: '13px' }}>{item.title}</Accordion.Header>
                      <Accordion.Body style={{ padding: '12px' }}>
                        {item.description && (
                          <p style={{ fontSize: '13px', color: C.gray600, marginBottom: '12px', whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                            {item.description}
                          </p>
                        )}
                        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '8px', overflow: 'hidden', backgroundColor: C.gray100 }}>
                          <iframe
                            src={activeKeys[category.stateKey] === item.itemKey ? `https://www.youtube.com/embed/${item.videoId}` : ''}
                            title={item.title}
                            allowFullScreen
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                          />
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Accordion.Body>
            </Accordion.Item>
          )) : (
            <div style={{ textAlign: 'center', padding: '32px', color: C.gray400 }}>
              <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔍</div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Nenhum tópico encontrado para <strong style={{ color: C.gray700 }}>"{searchTerm}"</strong>
              </p>
            </div>
          )}
        </Accordion>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: `1px solid ${C.gray100}`, padding: '12px 20px' }}>
        <button
          onClick={onHide}
          style={{
            padding: '8px 20px',
            backgroundColor: C.gray100,
            color: C.gray700,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            fontFamily: 'inherit',
          }}
        >
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DashboardPage;
